<template>
  <b-col
    class="form-box card-data-block"
    :class="customClass"
  >
    <div
      v-if="patient && patient.address"
      class="structure"
      :class="{ 'section-highlight': patientHasDataDiff }"
    >
      <span class="structure__heading"><i
        class="far fa-envelope mr-2"
        aria-hidden="true"
      />{{ $t('global.contactData') }}</span>
      <b-row class="mt-1">
        <b-col
          :class="{ 'highlight': hasPatientAddressDiff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientAddressCheckbox"
              v-model="address.address.selected"
              :unchecked-value="null"
            />
            <small>{{ $tc('global.address',1) }}</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientAddress }"
            class="collapsable-card-title inside"
          >
            {{ patient.address.address ? patient.address.address : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientPostalCodeDiff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientPostalCodeCheckbox"
              v-model="address.postalCode.selected"
              :unchecked-value="null"
            />
            <small>{{ $t('global.pc') }}</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientPostalCode }"
            class="collapsable-card-title inside"
          >
            {{ patient.address.postalCode ? patient.address.postalCode : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientMunicipalityCodeDiff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientMunicipalityCheckbox"
              v-model="address.municipality.selected"
              :unchecked-value="null"
              @change="onMunicipalityCheckboxInput()"
            />
            <small>{{ $tc('global.municipality',1) }}</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientMunicipality }"
            class="collapsable-card-title inside"
          >
            {{ !$_isEmpty(patient.address.municipality) ? patient.address.municipality.name : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientProvinceCodeDiff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientProvinceCheckbox"
              v-model="address.province.selected"
              :unchecked-value="null"
              @change="onProvinceCheckboxInput()"
            />
            <small>{{ $tc('global.province',1) }}</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientProvince }"
            class="collapsable-card-title inside"
          >
            {{ !$_isEmpty(patient.address.province) ? patient.address.province.name : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientCountryCodeDiff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientCountryCheckbox"
              v-model="address.country.selected"
              :unchecked-value="null"
              @change="onCountryCheckboxInput()"
            />
            <small>{{ $tc('global.country',1) }}</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientCountry }"
            class="collapsable-card-title inside"
          >
            {{ !$_isEmpty(patient.address.country) ? patient.address.country.name : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientMainPhoneDiff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientMainPhoneCheckbox"
              v-model="contactInformation.mainPhone.selected"
              :unchecked-value="null"
            />
            <small>{{ $tc('global.phone',1) }} 1</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientMainPhone }"
            class="collapsable-card-title inside"
          >
            {{ patient.contactInformation && patient.contactInformation.mainPhone ? patient.contactInformation.mainPhone : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientOtherPhone1Diff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientOtherPhone1Checkbox"
              v-model="contactInformation.otherPhone1.selected"
              :unchecked-value="null"
            />
            <small>{{ $tc('global.phone',1) }} 2</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientOtherPhone1 }"
            class="collapsable-card-title inside"
          >
            {{ patient.contactInformation && patient.contactInformation.otherPhone1 ? patient.contactInformation.otherPhone1 : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientOtherPhone2Diff }"
          lg="4"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientOtherPhone2Checkbox"
              v-model="contactInformation.otherPhone2.selected"
              :unchecked-value="null"
            />
            <small>{{ $tc('global.phone',1) }} 3</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientOtherPhone2 }"
            class="collapsable-card-title inside"
          >
            {{ patient.contactInformation && patient.contactInformation.otherPhone2 ? patient.contactInformation.otherPhone2 : '-' }}
          </h4>
        </b-col>
        <b-col
          :class="{ 'highlight': hasPatientEmailDiff }"
          cols="12"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-if="showPatientEmailCheckbox"
              v-model="contactInformation.email.selected"
              :unchecked-value="null"
            />
            <small>{{ $tc('global.email',1) }}</small>
          </div>
          <h4
            :class="{ 'item-selected': setItemSelectedClassToPatientEmail }"
            class="collapsable-card-title inside"
          >
            {{ patient.contactInformation && patient.contactInformation.email ? patient.contactInformation.email : '-' }}
          </h4>
        </b-col>
      </b-row>
    </div>
  </b-col>
</template>

<script>
export default {
    name: 'PatientAddressInfoCard',
    props: {
        patient: { type: Object, required: true },
        customClass: { type: Array },
        patientDataDiff: { type: Object },
        integrationPatient: { type: Boolean },
        localPatient: { type: Boolean },
        continueWithCaseCreation: { type: Boolean, default: false }
    },
    data() {
        return {
            address: {
                address: {
                    selected: true,
                    value: null
                },
                postalCode: {
                    selected: true,
                    value: null
                },
                municipality: {
                    selected: true,
                    value: null
                },
                province: {
                    selected: true,
                    value: null
                },
                country: {
                    selected: true,
                    value: null
                }
            },
            contactInformation: {
                mainPhone: {
                    selected: true,
                    value: null
                },
                otherPhone1: {
                    selected: true,
                    value: null
                },
                otherPhone2: {
                    selected: true,
                    value: null
                },
                email: {
                    selected: true,
                    value: null
                }
            }
        }
    },
    computed: {
        showPatientAddressCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientAddressDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientAddress() {
            return this.address.address.selected &&
            this.integrationPatient &&
            this.hasPatientAddressDiff
        },
        showPatientPostalCodeCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientPostalCodeDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientPostalCode() {
            return this.address.postalCode.selected &&
                this.integrationPatient &&
                this.hasPatientPostalCodeDiff
        },
        showPatientMunicipalityCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientMunicipalityCodeDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientMunicipality() {
            return this.address.municipality.selected &&
                this.integrationPatient &&
                this.hasPatientMunicipalityCodeDiff
        },
        showPatientProvinceCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientProvinceCodeDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientProvince() {
            return this.address.province.selected &&
                this.integrationPatient &&
                this.hasPatientProvinceCodeDiff
        },
        showPatientCountryCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientCountryCodeDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientCountry() {
            return this.address.country.selected &&
                this.integrationPatient &&
                this.hasPatientCountryCodeDiff
        },
        showPatientMainPhoneCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientMainPhoneDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientMainPhone() {
            return this.contactInformation.mainPhone.selected &&
                this.integrationPatient &&
                this.hasPatientMainPhoneDiff
        },
        showPatientOtherPhone1Checkbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientOtherPhone1Diff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientOtherPhone1() {
            return this.contactInformation.otherPhone1.selected &&
                this.integrationPatient &&
                this.hasPatientOtherPhone1Diff
        },
        showPatientOtherPhone2Checkbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientOtherPhone2Diff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientOtherPhone2() {
            return this.contactInformation.otherPhone2.selected &&
                this.integrationPatient &&
                this.hasPatientOtherPhone2Diff
        },
        showPatientEmailCheckbox() {
            let showCheckbox = this.integrationPatient
            if (this.continueWithCaseCreation) {
                showCheckbox = showCheckbox && this.hasPatientEmailDiff
            }
            return showCheckbox
        },
        setItemSelectedClassToPatientEmail() {
            return this.contactInformation.email.selected &&
                this.integrationPatient &&
                this.hasPatientEmailDiff
        },
        hasPatientAddressDiff() {
            let hasDiff = null
            if (!this.patientDataDiff.address?.address) {
                hasDiff = this.patientDataDiff.address.address === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.address.address !== this.patientDataDiff.address.address : true
            }
            return hasDiff
        },
        hasPatientPostalCodeDiff() {
            let hasDiff = null
            if (!this.patientDataDiff.address?.postalCode) {
                hasDiff = this.patientDataDiff.address.postalCode === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.address?.postalCode !== this.patientDataDiff.address.postalCode : true
            }
            return hasDiff
        },
        hasPatientMunicipalityCodeDiff() {
            let hasDiff = null
            if (this.$_isEmpty(this.patientDataDiff.address.municipality)) {
                hasDiff = this.localPatient ? false : this.patientDataDiff.address.municipality === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.address?.municipality?.code !== this.patientDataDiff.address.municipality?.code : true
            }
            return hasDiff
        },
        hasPatientProvinceCodeDiff() {
            let hasDiff = null
            if (this.$_isEmpty(this.patientDataDiff.address.province)) {
                hasDiff = this.localPatient ? false : this.patientDataDiff.address.province === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.address?.province?.code !== this.patientDataDiff.address.province?.code : true
            }
            return hasDiff
        },
        hasPatientCountryCodeDiff() {
            let hasDiff = null
            if (this.$_isEmpty(this.patientDataDiff.address.country)) {
                hasDiff = this.localPatient ? false : this.patientDataDiff.address.country === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.address?.country?.code !== this.patientDataDiff.address.country?.code : true
            }
            return hasDiff
        },
        hasPatientEmailDiff() {
            let hasDiff = null
            if (!this.patientDataDiff.contactInformation?.email) {
                hasDiff = this.patientDataDiff.contactInformation.email === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.contactInformation?.email !== this.patientDataDiff.contactInformation.email : true
            }
            return hasDiff
        },
        hasPatientMainPhoneDiff() {
            let hasDiff = null
            if (!this.patientDataDiff.contactInformation?.mainPhone) {
                hasDiff = this.patientDataDiff.contactInformation.mainPhone === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.contactInformation?.mainPhone !== this.patientDataDiff.contactInformation.mainPhone : true
            }
            return hasDiff
        },
        hasPatientOtherPhone1Diff() {
            let hasDiff = null
            if (!this.patientDataDiff.contactInformation?.otherPhone1) {
                hasDiff = this.patientDataDiff.contactInformation.otherPhone1 === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.contactInformation?.otherPhone1 !== this.patientDataDiff.contactInformation.otherPhone1 : true
            }
            return hasDiff
        },
        hasPatientOtherPhone2Diff() {
            let hasDiff = null
            if (!this.patientDataDiff.contactInformation?.otherPhone2) {
                hasDiff = this.patientDataDiff.contactInformation.otherPhone2 === null
            } else {
                hasDiff = this.localPatient
                    ? this.patient.contactInformation?.otherPhone2 !== this.patientDataDiff.contactInformation.otherPhone2 : true
            }
            return hasDiff
        },
        patientHasDataDiff() {
            return this.hasPatientAddressDiff ||
                this.hasPatientCountryCodeDiff ||
                this.hasPatientMunicipalityCodeDiff ||
                this.hasPatientPostalCodeDiff ||
                this.hasPatientProvinceCodeDiff ||
                this.hasPatientEmailDiff ||
                this.hasPatientMainPhoneDiff ||
                this.hasPatientOtherPhone1Diff ||
                this.hasPatientOtherPhone2Diff
        }
    },
    mounted() {
        this.initializePatientAddressInfoFields()
    },
    methods: {
        onMunicipalityCheckboxInput() {
            this.address.province.selected = !this.address.province.selected
            this.address.country.selected = !this.address.country.selected
        },
        onProvinceCheckboxInput() {
            this.address.country.selected = !this.address.country.selected
            this.address.municipality.selected = !this.address.municipality.selected
        },
        onCountryCheckboxInput() {
            this.address.municipality.selected = !this.address.municipality.selected
            this.address.province.selected = !this.address.province.selected
        },
        initializePatientAddressInfoFields() {
            this.address.address = {
                selected: this.hasPatientAddressDiff,
                value: this.patient.address.address
            }
            this.address.postalCode = {
                selected: this.hasPatientPostalCodeDiff,
                value: this.patient.address.postalCode
            }
            this.address.municipality = {
                selected: this.hasPatientMunicipalityCodeDiff,
                value: this.patient.address.municipality?.code ?? null
            }
            this.address.province = {
                selected: this.address.municipality.selected,
                value: this.patient.address.province?.code ?? null
            }
            this.address.country = {
                selected: this.address.province.selected,
                value: this.patient.address.country?.code ?? null
            }
            this.contactInformation.mainPhone = {
                selected: this.hasPatientMainPhoneDiff,
                value: this.patient.contactInformation.mainPhone
            }
            this.contactInformation.otherPhone1 = {
                selected: this.hasPatientOtherPhone1Diff,
                value: this.patient.contactInformation.otherPhone1
            }
            this.contactInformation.otherPhone2 = {
                selected: this.hasPatientOtherPhone2Diff,
                value: this.patient.contactInformation.otherPhone2
            }
            this.contactInformation.email = {
                selected: this.hasPatientEmailDiff,
                value: this.patient.contactInformation.email
            }
        }
    }
}
</script>

<style scoped>

</style>
